/**
 * In-house training: the three courses AGICAFET runs at a client's office
 * (owner, 2026-10-04). Topics come from the owner's course posters
 * (assets-src/sources/training/); the page shows each poster's illustrated
 * scene and says the words as text.
 *
 * Claims (DESIGN.md → Claims): NVIDIA runs its own certification exams — the
 * GenAI course prepares for them, it does not grant them, and it is not an
 * official NVIDIA course. The badges are NVIDIA's own artwork, shown as the
 * certifications the course prepares for. The Qwen Ambassador title is the
 * founder's own.
 */
export interface CertBadge {
  src: string;
  alt: string;
}

export interface Mark {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface Course {
  id: string;
  /** Who it is for, shown above the title. */
  audience: string;
  title: string;
  /** The subtitle under the title. */
  sub: string;
  line: string;
  topics: readonly string[];
  /** The poster's scene, built by scripts/build-images.py at 640 and 960 px. */
  image: string;
  alt: string;
  /** The certifications the course prepares for, grouped by level. */
  certs?: readonly { level: string; badges: readonly CertBadge[] }[];
  /** The course's own mark, and the tools it is hands-on with. */
  logo?: Mark;
  tools?: { label: string; marks: readonly Mark[] };
}

export const COURSES: readonly Course[] = [
  {
    id: 'genai-agentic',
    audience: 'For engineers and AI teams',
    title: 'Generative AI LLMs & Agentic AI',
    sub: 'Associate and Professional certification preparation',
    line: 'Hands-on preparation for four NVIDIA certifications, led by an NVIDIA-Certified Professional.',
    topics: [
      'LLMs and multimodal generative AI, from fundamentals to use cases',
      'Agentic AI: tool use, memory and planning',
      'Hands-on with NVIDIA tools and frameworks',
    ],
    image: 'training-genai-agentic',
    alt: 'Illustration: a trainer presenting an LLM connected to tool use, memory and planning',
    certs: [
      {
        level: 'Associate',
        badges: [
          { src: '/img/nvidia-cert-associate-genai-llms.webp', alt: 'NVIDIA-Certified Associate: Generative AI LLMs' },
          { src: '/img/nvidia-cert-associate-genai-multimodal.webp', alt: 'NVIDIA-Certified Associate: Generative AI Multimodal' },
        ],
      },
      {
        level: 'Professional',
        badges: [
          { src: '/img/nvidia-cert-professional-genai-llms.webp', alt: 'NVIDIA-Certified Professional: Generative AI LLMs' },
          { src: '/img/nvidia-cert-professional-agentic-ai.webp', alt: 'NVIDIA-Certified Professional: Agentic AI' },
        ],
      },
    ],
  },
  {
    id: 'ai-native-sdlc',
    audience: 'For developers and IT teams',
    title: 'AI-Native SDLC',
    sub: 'From planning to production with Qoder, QoderWake and Qwen Code',
    line: 'Plan, code, test and ship with coding agents. A hands-on workshop led by our founder, a Qwen Ambassador.',
    topics: [
      'Qwen Code and coding-agent workflows: plan, code, test, iterate',
      'Qoder and QoderWake: always-on AI employees in action',
      'Tool use, MCP and integrations with your own tools and data',
    ],
    image: 'training-ai-native-sdlc',
    alt: 'Illustration: a trainer presenting Qwen Code, coding-agent workflows, Qoder and QoderWake, MCP and a hands-on demo',
    logo: { src: '/img/mark-qwen-sdlc.webp', alt: 'Qwen SDLC', width: 413, height: 192 },
    tools: {
      label: 'Hands-on with',
      marks: [
        { src: '/img/mark-qoder.webp', alt: 'Qoder', width: 393, height: 96 },
        { src: '/img/mark-qoderwake.webp', alt: 'QoderWake', width: 604, height: 96 },
      ],
    },
  },
  {
    id: 'onprem-exec',
    audience: 'For executives and decision makers',
    title: 'On-Prem AI for Executives',
    sub: 'Strategy, security and business value',
    line: 'See the opportunity, see the whole picture, and decide with confidence.',
    topics: [
      'Why on-prem AI, and the business use cases',
      'Model choice, infrastructure, security and governance',
      'Cost, ROI and a roadmap to adoption',
    ],
    image: 'training-onprem-exec',
    alt: 'Illustration: a trainer presenting on-prem local AI: users, AI applications, local models, the orchestrator and on-prem infrastructure',
  },
];
